import { Box, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { cci as cciApi, vendor as vendorApi } from '../../../api';
import { DOLLAR_NO_DECIMAL, MAX_PAGINATION_FILTER } from '../../../constants';
import { useApi, useAuth, useInternationalization } from '../../../hooks';
import { MemberCommitmentReportDetailEntity } from '../../../models/Cci';
import {
  CommitmentEventFilter,
  ExportType,
  LanguageCode,
  MemberCommitmentReportFilter,
  MemberCommitmentReportGrouping,
  OrderBy,
  PermissionKey,
  RebateTypeAndUnitType,
  Styles,
} from '../../../types';
import { numberFormatter } from '../../../utils/formatters';
import { downloadBlob, formatShortDate } from '../../../utils/helper';
import { ExportButton } from '../../ExportButton';
import { Autocomplete, MultiSelect } from '../../Form';
import { LayoutPage } from '../../Layout';
import { Table, TableColumn } from '../../Table';

const styles: Styles = {
  selectSmallContainer: {
    width: {
      xs: '100%',
      md: '220px',
    },
  },
  selectLargeContainer: {
    width: '100%',
  },
  textContainer: {
    width: {
      xs: '100%',
      sm: '450px',
    },
  },
};

const defaultFilter: MemberCommitmentReportFilter = {
  grouping: MemberCommitmentReportGrouping.None,
  ...MAX_PAGINATION_FILTER,
};

const commitmentEventDefaultFilter: CommitmentEventFilter = {
  isArchived: false,
  ...MAX_PAGINATION_FILTER,
};

export const MemberCommitmentHistory = () => {
  const { t, getTranslation, currentLanguage } = useInternationalization();
  const { user } = useAuth();
  const [filter, setFilter] = useState(defaultFilter);
  const {
    data,
    isLoading,
    refresh: refreshMemberCommitments,
  } = useApi(cciApi.getAllMemberCommitmentReportDetails, null, filter);
  const {
    data: events,
    isLoading: isEventsLoading,
    refresh: refreshEvents,
  } = useApi(cciApi.getAllCommitmentEvents, null, commitmentEventDefaultFilter);
  const { data: categories, isLoading: isCategoriesLoading } = useApi(
    cciApi.getCommitmentCategorySuggestions,
    null,
    '',
  );
  const { call: downloadExport } = useApi(cciApi.exportMemberCommitments, {
    successKey: 'common:success.action',
  });

  const eventsOptions = useMemo(
    () => events?.data.map((event) => ({ value: event.id, label: event.year.toString() })) ?? [],
    [events],
  );

  const categoryOptions = useMemo(
    () => categories?.data.map((category) => ({ value: category.id, label: getTranslation(category, 'name') })) ?? [],
    [getTranslation, categories],
  );

  // Refresh data when user changes selected member
  useEffect(() => {
    refreshMemberCommitments();
    refreshEvents();
  }, [user?.memberId, refreshMemberCommitments, refreshEvents]);

  const onExportClick = async (exportType: ExportType = ExportType.PDF) => {
    const fileData = await downloadExport(exportType);
    if (fileData) {
      downloadBlob(fileData.filename ?? 'export.pdf', fileData.data);
    }
  };

  const onPaginationChange = (pageNumber: number, pageSize: number) =>
    setFilter((prev) => ({ ...prev, pageNumber: prev.pageSize !== pageSize ? 1 : pageNumber, pageSize }));

  const onSortChange = (orderBy: OrderBy[]) =>
    setFilter((prev) => ({ ...prev, orderBy: orderBy.length ? orderBy : undefined }));

  const onEventsChange = (values: number[]) => setFilter((prev) => ({ ...prev, commitmentEventIds: values }));

  const onCategoriesChange = (values: number[]) => setFilter((prev) => ({ ...prev, commitmentCategoryIds: values }));

  const moneyFormatter = useMemo(
    () =>
      numberFormatter(
        currentLanguage,
        t(`common:rebateTypeAndUnitType.${RebateTypeAndUnitType.Dollar}`),
        DOLLAR_NO_DECIMAL,
        currentLanguage !== LanguageCode.en,
      ),
    [currentLanguage, t],
  );

  const renderSearch = () => (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={{ xs: 1, md: 2 }}
      mb={2}
      alignItems={{ xs: 'inherit', md: 'flex-end' }}
    >
      <Box sx={styles.selectSmallContainer}>
        <MultiSelect
          onConfirm={onEventsChange}
          options={eventsOptions}
          label={t('cci:memberCommitmentsHistory.filter.years')}
        />
      </Box>
      <Box sx={styles.selectSmallContainer}>
        <MultiSelect
          onConfirm={onCategoriesChange}
          options={categoryOptions}
          label={t('cci:memberCommitmentsHistory.filter.categories')}
        />
      </Box>
      <Autocomplete
        minSearchTextLength={1}
        value={filter.vendorNameOrNumber}
        sx={styles.textContainer}
        noOptionsText={t('vendor:search.notFound')}
        apiFunction={vendorApi.getSuggestions}
        label={t('vendor:search.title')}
        placeholder={t('vendor:search.placeholder')}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return `${option.fullNumber ? option.fullNumber + ' - ' : ''}${option.name}`;
        }}
        onApplySearch={(searchText) =>
          setFilter((prev) => ({
            ...prev,
            vendorNameOrNumber: searchText ?? undefined,
            vendorId: undefined,
            pageNumber: 1,
          }))
        }
        onSelectItem={(item) =>
          setFilter((prev) => ({
            ...prev,
            vendorId: item?.id,
            vendorNameOrNumber: item ? `${item.fullNumber ? item.fullNumber + ' - ' : ''}${item.name}` : undefined,
            pageNumber: 1,
          }))
        }
      />
    </Stack>
  );

  const renderPageActions = () => (
    <ExportButton displayMenu={false} defaultExportType={ExportType.PDF} onExportClick={onExportClick} />
  );

  return (
    <LayoutPage
      display="Tab"
      permissions={{ keys: PermissionKey.CciViewCommitments }}
      title={t('cci:memberCommitmentsHistory.title')}
      rightTitle={renderPageActions()}
    >
      {renderSearch()}
      <Table
        data={data ?? []}
        translationNamespace="cci:memberCommitmentsHistory"
        paginationFilter={filter}
        onPaginationChange={onPaginationChange}
        sortColumns={filter.orderBy}
        onSortChange={onSortChange}
        isLoading={isLoading || isEventsLoading || isCategoriesLoading}
      >
        <TableColumn
          type="custom"
          id="year"
          render={(item: MemberCommitmentReportDetailEntity) => item?.event?.year?.toString() ?? ''}
          sortable
        />
        <TableColumn
          type="custom"
          id="category"
          render={(item: MemberCommitmentReportDetailEntity) =>
            item?.category ? getTranslation(item.category, 'name') : ''
          }
          sortable
        />
        <TableColumn
          type="custom"
          id="vendor"
          render={(item: MemberCommitmentReportDetailEntity) => item?.vendor?.name ?? ''}
          sortable
        />
        <TableColumn
          type="custom"
          id="isCurrent"
          render={(item: MemberCommitmentReportDetailEntity) =>
            t(`common:boolean.${(!!(item?.isCurrent || false)).toString()}`)
          }
          sortable
        />
        <TableColumn
          type="custom"
          id="willSupport"
          render={(item: MemberCommitmentReportDetailEntity) =>
            t(`common:boolean.${(!!(item?.willSupport || false)).toString()}`)
          }
          sortable
        />
        <TableColumn
          type="custom"
          id="volume"
          render={(item: MemberCommitmentReportDetailEntity) =>
            item?.volume != null ? moneyFormatter(item?.volume).formatted.join('') : '-'
          }
          sortable
        />
        <TableColumn
          type="custom"
          id="createdDate"
          render={(item: MemberCommitmentReportDetailEntity) =>
            t('cci:memberCommitmentsHistory.table.committedValue', {
              name: item.createdBy,
              date: formatShortDate(item.createdDate),
            })
          }
          sortable
        />
        <TableColumn
          type="custom"
          id="modifiedDate"
          render={(item: MemberCommitmentReportDetailEntity) =>
            t('cci:memberCommitmentsHistory.table.modifiedValue', {
              name: item.modifiedBy,
              date: formatShortDate(item.modifiedDate),
            })
          }
          sortable
        />
      </Table>
    </LayoutPage>
  );
};
